// last-modified-anim.ts — Utilitaire d'animation post-MAJ.
//
// Pattern :
//   1. Côté page d'édition d'une entité (action, projet, programme, etc.) :
//      avant de naviguer vers la liste, appeler markLastModified(entity, id).
//   2. Côté liste : juste après le render initial, appeler
//      consumeLastModifiedAnimation(entity, rowFinder) pour faire pulser
//      brièvement la ligne dont l'id correspond.
//
// Le drapeau est posé dans sessionStorage (clef `lastModified:<entity>`) avec
// un TTL court (5 s) pour éviter qu'une visite ultérieure de la liste anime
// à contre-temps. Le drapeau est consommé une seule fois.
//
// Si plusieurs entités sont touchées en série (cas peu fréquent, par ex.
// quick-edit en boucle), seule la dernière sera animée — limitation
// volontaire pour rester simple.

interface LastModifiedFlag {
  id?: number;
  ts?: number;
}

function _storageKey(entity: string): string {
  return `lastModified:${entity}`;
}

/** Pose le drapeau "dernière entité modifiée" pour animation à la liste. */
export function markLastModified(entity: string, id: number): void {
  try {
    sessionStorage.setItem(_storageKey(entity), JSON.stringify({ id, ts: Date.now() }));
  } catch {
    // sessionStorage indisponible (mode privé Safari, quota plein) :
    // l'animation est perdue, comportement gracieux.
  }
}

/**
 * Consomme le drapeau et anime la ligne correspondante.
 * @param entity nom de l'entité (ex. 'actions', 'projects')
 * @param rowFinder fonction qui retourne l'élément DOM de la ligne pour un id
 * @param ttlMs TTL au-delà duquel le drapeau est ignoré (défaut 5 s)
 */
export function consumeLastModifiedAnimation(
  entity: string,
  rowFinder: (id: number) => HTMLElement | null,
  ttlMs = 5000,
): void {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(_storageKey(entity));
  } catch {
    return;
  }
  if (!raw) return;
  try {
    sessionStorage.removeItem(_storageKey(entity));
  } catch {
    /* noop */
  }
  let data: LastModifiedFlag;
  try {
    data = JSON.parse(raw) as LastModifiedFlag;
  } catch {
    return;
  }
  if (!data.id || !data.ts) return;
  if (Date.now() - data.ts > ttlMs) return;
  _ensureStyles();
  const row = rowFinder(data.id);
  if (row) {
    row.classList.add('last-modified-highlight');
    // Le keyframe dure 2.4s ; on retire la classe ensuite pour ne pas
    // bloquer une éventuelle ré-animation sur la même ligne plus tard.
    setTimeout(() => row.classList.remove('last-modified-highlight'), 2600);
  }
}

// ─── Variante multi-id ────────────────────────────────────────────────────
// Même mécanique mais le drapeau porte une liste d'ids. Utilisé quand une
// action utilisateur ajoute plusieurs lignes d'un coup (ex. rattacher 5
// actions à une réunion via le mode multi-select). Clé séparée pour ne pas
// piétiner le drapeau "single-id" existant.

interface LastModifiedManyFlag {
  ids?: number[];
  ts?: number;
}

function _storageKeyMany(entity: string): string {
  return `lastModifiedMany:${entity}`;
}

/** Pose le drapeau "N entités modifiées" pour animation à la liste. */
export function markLastModifiedMany(entity: string, ids: ReadonlyArray<number>): void {
  if (!ids.length) return;
  try {
    sessionStorage.setItem(
      _storageKeyMany(entity),
      JSON.stringify({ ids: Array.from(ids), ts: Date.now() }),
    );
  } catch {
    /* noop */
  }
}

/**
 * Consomme le drapeau multi-id et anime chaque ligne correspondante.
 */
export function consumeLastModifiedAnimationMany(
  entity: string,
  rowFinder: (id: number) => HTMLElement | null,
  ttlMs = 5000,
): void {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(_storageKeyMany(entity));
  } catch {
    return;
  }
  if (!raw) return;
  try {
    sessionStorage.removeItem(_storageKeyMany(entity));
  } catch {
    /* noop */
  }
  let data: LastModifiedManyFlag;
  try {
    data = JSON.parse(raw) as LastModifiedManyFlag;
  } catch {
    return;
  }
  if (!data.ids || !data.ids.length || !data.ts) return;
  if (Date.now() - data.ts > ttlMs) return;
  _ensureStyles();
  for (const id of data.ids) {
    const row = rowFinder(id);
    if (!row) continue;
    row.classList.add('last-modified-highlight');
    setTimeout(() => row.classList.remove('last-modified-highlight'), 2600);
  }
}

let _stylesInjected = false;
function _ensureStyles(): void {
  if (_stylesInjected) return;
  if (document.getElementById('last-modified-anim-styles')) {
    _stylesInjected = true;
    return;
  }
  const s = document.createElement('style');
  s.id = 'last-modified-anim-styles';
  s.textContent = `
    @keyframes last-modified-pulse {
      0%   { background:transparent; }
      20%  { background:#fef3c7; }
      50%  { background:transparent; }
      80%  { background:#fef3c7; }
      100% { background:transparent; }
    }
    /* Anime la ligne entière ; on cible aussi les <td> car certains
       layouts posent une background sur td qui peut sinon masquer
       l'animation du tr. */
    tr.last-modified-highlight, tr.last-modified-highlight > td {
      animation: last-modified-pulse 2.4s ease-in-out;
    }
    /* Hors tableau (ex. cartes projet/programme) : on accepte aussi
       l'animation directement sur l'élément. */
    .last-modified-highlight:not(tr) {
      animation: last-modified-pulse 2.4s ease-in-out;
    }
  `;
  document.head.appendChild(s);
  _stylesInjected = true;
}
